import React, { useState, useEffect } from "react";
import { UserPlus, Trash2, Shield, Loader2, AlertTriangle, Users } from "lucide-react";
import { safeJsonRequest } from "../../utils/safeFetch";

/**
 * UserManagementPanel — Super admin can add/remove admin users.
 */
const UserManagementPanel = ({ token, currentEmail, currentRole }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", role: "admin" });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState("");

  const isSuperAdmin = currentRole === "super_admin";

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await safeJsonRequest("/api/admin/users", { token });
      setUsers(data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.password.trim()) return;
    setCreating(true);
    setError("");
    try {
      await safeJsonRequest("/api/admin/users", {
        method: "POST",
        body: formData,
        token,
      });
      setFormData({ email: "", password: "", name: "", role: "admin" });
      setShowForm(false);
      loadUsers();
    } catch (err) {
      setError(typeof err.detail === "string" ? err.detail : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Remove admin user ${email}? This cannot be undone.`)) return;
    setDeleting(userId);
    try {
      await safeJsonRequest(`/api/admin/users/${userId}`, { method: "DELETE", token });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      alert(typeof err.detail === "string" ? err.detail : "Failed to delete user");
    } finally {
      setDeleting(null);
    }
  };

  const roleColor = (role) =>
    role === "super_admin" ? "text-amber-400 bg-amber-900/40" : "text-blue-400 bg-blue-900/40";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-300 font-medium">{users.length} admin users</span>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs text-white font-medium transition-colors flex items-center gap-1.5"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add Admin User
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-3 mb-4 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">Only super admins can add or remove users. Contact a super admin for changes.</p>
        </div>
      )}

      {/* Create User Form */}
      {showForm && isSuperAdmin && (
        <form onSubmit={handleCreate} className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-4" data-testid="create-user-form">
          {error && (
            <div className="mb-3 p-2.5 bg-red-900/40 border border-red-700 rounded-lg flex items-start gap-2" data-testid="create-user-error">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="user@sdorawellbeing.com"
                required
                data-testid="new-user-email"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Temporary Password *</label>
              <input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Temporary password"
                required
                data-testid="new-user-password"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Full name"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="new-user-role"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); setError(""); }} className="px-3 py-2 bg-slate-700 text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-600">Cancel</button>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-500 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="create-user-submit"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              Create User
            </button>
          </div>
        </form>
      )}

      {/* User List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <span className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-center py-16 text-sm text-slate-500">No admin users found</p>
      ) : (
        <div className="space-y-2">
          {users.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg"
              data-testid={`user-row-${user.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{user.name || user.email}</p>
                  <p className="text-xs text-slate-400">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor(user.role)}`}>
                  {user.role === "super_admin" ? "Super Admin" : "Admin"}
                </span>
                {isSuperAdmin && user.email !== currentEmail && (
                  <button
                    onClick={() => handleDelete(user.id, user.email)}
                    disabled={deleting === user.id}
                    className="p-1.5 rounded-lg hover:bg-red-900/40 transition-colors text-slate-500 hover:text-red-400 disabled:opacity-50"
                    title="Remove user"
                    data-testid={`delete-user-${user.id}`}
                  >
                    {deleting === user.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                )}
                {user.email === currentEmail && (
                  <span className="text-[10px] text-slate-600 italic">You</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserManagementPanel;
